import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import cx from 'classnames';

import {
  DROPPING_OFF,
  HIDE_ORDER_HOURS,
  PICKING_UP,
  FAILED_DELIVERY,
} from 'constants/new-order-status';
import {
  isAfterHours,
  isCancelledStatus,
  isCompletedStatus,
} from 'common/utils/status';
import { getExpectationMessage } from 'common/utils/expectation-management';
import {
  absAmount,
  formatPrice,
  isNegativeAmount,
} from 'common/utils/currency';

import Eta from './eta';
import StatusProgressBar from './status-progress-bar';
import StatusLogo from './status-logo';
import InfoBanner from '../banner/info-banner';

import styles from './status-card.less';
import exclamationSvg from '../../public/images/exclamation.svg';

const StatusCard = ({
  isExpanded,
  status,
  eta,
  endTime,
  schedule,
  description,
  expectationId,
  itemTypeId,
  payment,
  userType,
  hideCashlessPOD,
}) => {
  const containerClassName = cx(
    styles.container,
    !isExpanded && styles.nonExpandedContainer,
    isExpanded && hideCashlessPOD && styles.expandedContainer,
  );

  const upperCaseStatus = status.toUpperCase();

  const infoText = useMemo(
    () => {
      if (status === FAILED_DELIVERY && !hideCashlessPOD) {
        return {
          newUI: true,
          body: I18n.t('statusCard.info.resumeDelivery'),
        };
      }

      const showCashExpectation =
        (status === DROPPING_OFF || status === PICKING_UP) &&
        payment.payOnDelivery &&
        !!parseFloat(payment.payOnDelivery.totalCashAmount);

      if (showCashExpectation) {
        const total = payment.payOnDelivery.totalCashAmount;
        const titleKey = isNegativeAmount(total)
          ? 'expectationManagement.payment.collect.title'
          : 'expectationManagement.payment.pay.title';

        return {
          newUI: false,
          body: I18n.t(titleKey, {
            price: formatPrice({
              amount: absAmount(total),
              symbol: payment.symbol,
            }),
          }),
        };
      }

      return getExpectationMessage(expectationId);
    },
    [expectationId, payment, status],
  );

  const hideProgress =
    isCancelledStatus(upperCaseStatus) ||
    (isCompletedStatus(upperCaseStatus) &&
      isAfterHours(endTime, HIDE_ORDER_HOURS));

  return (
    <div className={containerClassName}>
      <div className={styles.statusContainer}>
        <Eta
          time={eta}
          schedule={schedule}
          endTime={endTime}
          description={description}
          status={upperCaseStatus}
          hideCashlessPOD={hideCashlessPOD}
        />

        <StatusLogo
          status={upperCaseStatus}
          expectationId={expectationId}
          itemTypeId={itemTypeId}
          userType={userType}
        />
      </div>

      {!hideProgress && <StatusProgressBar status={upperCaseStatus} />}

      {isExpanded &&
        infoText &&
        (infoText.newUI ? (
          <InfoBanner
            icon={exclamationSvg}
            containerStyle={{
              backgroundColor: '#EBFAFF',
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              borderBottomLeftRadius: '16px',
              borderBottomRightRadius: '16px',
            }}
            text={infoText}
          />
        ) : (
          <p className={styles.expectationMessage}>{infoText.body}</p>
        ))}
    </div>
  );
};

StatusCard.propTypes = {
  isExpanded: PropTypes.bool,
  status: PropTypes.string.isRequired,
  eta: PropTypes.number.isRequired,
  endTime: PropTypes.number,
  description: PropTypes.string,
  expectationId: PropTypes.number,
  itemTypeId: PropTypes.number,
  userType: PropTypes.shape({
    isSender: PropTypes.bool,
    isReceiver: PropTypes.bool,
  }),
  schedule: PropTypes.shape({
    from: PropTypes.number,
    to: PropTypes.number,
  }),
  payment: PropTypes.shape({
    symbol: PropTypes.string,
    total: PropTypes.string,
    payOnDelivery: PropTypes.shape({
      totalAmount: PropTypes.string,
      totalCashAmount: PropTypes.string,
      totalCashlessAmount: PropTypes.string,
    }),
  }),
  hideCashlessPOD: PropTypes.bool,
};

StatusCard.defaultProps = {
  isExpanded: false,
  description: '',
  endTime: 0,
  expectationId: 0,
  itemTypeId: 0,
  userType: { isSender: false, isReceiver: false },
  schedule: null,
  payment: {
    symbol: '',
    total: '',
    payOnDelivery: {
      totalAmount: '',
      totalCashAmount: '',
      totalCashlessAmount: '',
    },
  },
  hideCashlessPOD: false,
};

export default StatusCard;
